import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';

pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.BASE_URL || '/'}js/pdf.worker.min.js`;

/**
 * PDF.js 解析出的單一文字元素結構
 * @typedef {Object} PdfTextItem
 * @property {string} str - 文字內容
 * @property {string} dir - 文字方向 (ltr / rtl)
 * @property {number} width - 該文字區塊寬度 (pt)
 * @property {number} height - 該文字區塊高度 (pt)
 * @property {Array<number>} transform - 座標變換矩陣 [scaleX, skewY, skewX, scaleY, x, y]
 */

/**
 * 經解析後的 PDF 單頁結構
 * @typedef {Object} ParsedPage
 * @property {number} pageNumber - 頁碼 (從 1 開始)
 * @property {string} fullText - 該頁面所有文字合併後的純文字字串
 * @property {Array<PdfTextItem>} items - 該頁包含的原始文字元素集合
 */

/**
 * 單一檢驗項目數值結構
 * @typedef {Object} LabTestItem
 * @property {string} item - 檢驗項目簡稱或全名 (如 "RBC", "GLU", "ALT")
 * @property {string} rawResult - PDF 原始結果字串 (如 "7.56 M/μL", "< 10 U/L")
 * @property {string|number} value - 提取之數值部分，純數字自動轉為 number，帶符號則保留 string (如 7.56 或 "< 10")
 * @property {string} unit - 檢驗單位 (如 "M/μL", "mg/dL", "%")
 * @property {string} referenceInterval - 參考基準區間字串 (如 "5.5 - 8.5 M/μL")
 * @property {string} previousResult - 歷史檢驗原始字串 (如 "6.58 M/μL")
 * @property {string|number} previousValue - 歷史檢驗數值 (如 6.58)
 * @property {string} previousUnit - 歷史檢驗單位 (如 "M/μL")
 */

/**
 * 單一台檢驗設備之報告結構
 * @typedef {Object} LabDeviceReport
 * @property {number} pageNumber - 報告所在頁碼
 * @property {string} deviceName - 設備名稱 (如 "Catalyst One", "ProCyte Dx")
 * @property {string} testDateTime - 當次檢驗時間字串 (如 "2024-03-20 14:30")
 * @property {string} previousDateTime - 上次檢驗時間字串
 * @property {string} rawHeader - 設備原始標題行文字
 * @property {Array<LabTestItem>} tests - 該設備所測得的所有檢驗項目列表
 */

/**
 * 病患／動物基本資料結構
 * @typedef {Object} PatientInfo
 * @property {string} name - 寵物／動物名稱
 * @property {string} case_no - 畜主姓名或病歷號
 * @property {string} animal_type - 畜別 (如 犬、貓、野生動物)
 * @property {string} species - 種別／品種
 * @property {string} doctor - 主治或檢驗醫師姓名
 * @property {string} gender - 性別
 * @property {string} weight - 體重
 * @property {string} age - 年齡
 * @property {string} hospital_name - 檢驗機構／醫院名稱
 * @property {string} printed_datetime - 列印時間
 */

/**
 * 單一檢驗細項資料庫結構 (Storage Item)
 * @typedef {Object} StorageExamItem
 * @property {string} item - 檢驗項目代碼 (如 "RBC", "GLU")
 * @property {number|string} value - 數值
 * @property {string} unit - 單位
 * @property {string} reference_interval - 參考範圍
 * @property {string} previous_result - 歷史結果原始字串
 * @property {number|string} previous_value - 歷史數值
 * @property {string} raw - 原始文字
 */

/**
 * 單一設備檢驗報告儲存結構 (Storage Report)
 * @typedef {Object} StorageExamReport
 * @property {number} exam_device_id - 設備代碼 (3: Catalyst One 生化, 4: ProCyte Dx 血球, 0: 未知)
 * @property {string} device_name - 設備名稱
 * @property {string} device_type - 設備類型 ("CHEM" | "CBC" | "UNKNOWN")
 * @property {number} page_number - 來源頁碼
 * @property {string} exam_datetime - 檢驗時間
 * @property {string} previous_datetime - 上次檢驗時間
 * @property {string} printed_datetime - 列印時間
 * @property {Object<string, number|string>} exam_result - Key-Value 檢驗結果字典 (例: { "RBC": 7.56, "HCT": 45.2 })
 * @property {Array<StorageExamItem>} items - 包含完整細節的項目陣列
 */

/**
 * 資料庫儲存用標準 JSON 結構
 * @typedef {Object} StorageJsonResult
 * @property {string} parsed_at - 解析完成之 ISO 8601 時間戳記
 * @property {PatientInfo} patient_info - 病患基本資訊
 * @property {Array<StorageExamReport>} exam_reports - 依設備分組之檢驗報告清單
 */

/**
 * PDF 檔案解析主回傳結構
 * @typedef {Object} ParsedPdfResult
 * @property {Object} pdfDoc - PDF.js 原始 Document 物件
 * @property {Array<ParsedPage>} parsedPages - 各頁原始文字與元素陣列
 * @property {Array<LabDeviceReport>} labReports - 設備檢驗報告結構
 * @property {StorageJsonResult} storageData - 直接可用於資料庫儲存或 API 送出的標準資料結構
 * @property {Object} metaData - 檔案摘要資訊 (fileName, fileSize, totalPages, totalCharacters)
 */

/**
 * 格式化檔案大小 (將 Byte 轉為人類易讀的單位)
 * 
 * @param {number} bytes - 檔案位元組大小 (Byte)
 * @returns {string} 格式化字串 (如 "35.4 KB", "1.2 MB")
 * 
 * @example
 * formatFileSize(1048576); // => "1 MB"
 */
export const formatFileSize = (bytes) => {
  try {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  } catch (error) {
    console.error('[pdfLabParser] formatFileSize error:', error);
    return '0 B';
  }
};

/**
 * 拆分檢驗結果字串為「數值」與「單位」
 * 支援一般數字、小數、正負號以及比較符號 (如 "< 10", "> 200", "7.56 M/μL")
 * 
 * @param {string} text - 原始結果文字
 * @returns {{ value: string, unit: string }} 拆分後的數值與單位物件
 * 
 * @example
 * parseValueAndUnit("7.56 M/μL"); // => { value: "7.56", unit: "M/μL" }
 * parseValueAndUnit("< 10 U/L");   // => { value: "< 10", unit: "U/L" }
 */
export const parseValueAndUnit = (text) => {
  try {
    if (!text || typeof text !== 'string') return { value: '', unit: '' };
    const trimmed = text.trim();
    const match = trimmed.match(/^([<>]?\s*[-+]?\d*\.?\d+)\s*(.*)$/);
    if (match) {
      return {
        value: match[1].trim(),
        unit: match[2].trim()
      };
    }
    return { value: trimmed, unit: '' };
  } catch (error) {
    console.error('[pdfLabParser] parseValueAndUnit error:', error);
    return { value: text || '', unit: '' };
  }
};

/**
 * 從 PDF 各頁文字中正則萃取病患／動物基本資料
 * 包含寵物名字、病歷/畜主、畜別、品種、主治醫師、性別、體重、年齡等
 * 
 * @param {Array<ParsedPage>} pages - PDF.js 解析出的頁面陣列
 * @returns {PatientInfo} 病患基本資料物件
 * 
 * @example
 * const patientInfo = extractPatientInfo(parsedPages);
 */
export const extractPatientInfo = (pages) => {
  try {
    const defaultInfo = {
      name: '',
      case_no: '',
      animal_type: '',
      species: '',
      doctor: '',
      gender: '',
      weight: '',
      age: '',
      hospital_name: '',
      printed_datetime: ''
    };

    if (!Array.isArray(pages) || pages.length === 0) return defaultInfo;

    const fullText = pages.map(p => p.fullText || '').join(' ');

    const nameMatch = fullText.match(/寵物名字:\s*([^\s]+)/);
    const caseMatch = fullText.match(/畜主名字:\s*([^\s]+)/);
    const animalTypeMatch = fullText.match(/畜別:\s*([^\s]+)/);
    const speciesMatch = fullText.match(/種別:\s*([^\s]+)/);
    const doctorMatch = fullText.match(/醫師姓名:\s*([^\s]+)/);
    const genderMatch = fullText.match(/性別:\s*([^\s]+)/);
    const weightMatch = fullText.match(/體重:\s*([^\s]+)/);
    const ageMatch = fullText.match(/年齡:\s*([^\s]+)/);
    const hospitalMatch = fullText.match(/(野灣[^\s]+醫院)/);
    const printedMatch = fullText.match(/已列印:\s*([^頁]+)/);

    return {
      name: nameMatch ? nameMatch[1].trim() : '',
      case_no: caseMatch ? caseMatch[1].trim() : '',
      animal_type: animalTypeMatch ? animalTypeMatch[1].trim() : '',
      species: speciesMatch ? speciesMatch[1].trim() : '',
      doctor: doctorMatch ? doctorMatch[1].trim() : '',
      gender: genderMatch ? genderMatch[1].trim() : '',
      weight: weightMatch ? weightMatch[1].trim() : '',
      age: ageMatch ? ageMatch[1].trim() : '',
      hospital_name: hospitalMatch ? hospitalMatch[1].trim() : '',
      printed_datetime: printedMatch ? printedMatch[1].trim() : ''
    };
  } catch (error) {
    console.error('[pdfLabParser] extractPatientInfo error:', error);
    return {
      name: '',
      case_no: '',
      animal_type: '',
      species: '',
      doctor: '',
      gender: '',
      weight: '',
      age: '',
      hospital_name: '',
      printed_datetime: ''
    };
  }
};

/**
 * 依據 PDF 頁面元素空間座標（X, Y 軸）進行多列分組與欄位萃取
 * 演算法特點：
 * 1. 識別設備標題列與檢驗時間戳記
 * 2. 以 2.5pt 容差群組化同一水平線 (Y軸) 的文字元素
 * 3. 依據 X 座標落點自動判定欄位：
 *    - X < 120: 項目名稱 (Item Name)
 *    - 120 <= X < 185: 當次結果 (Result + Unit)
 *    - 185 <= X < 350: 參考區間 (Reference Interval)
 *    - X >= 460: 歷史檢驗紀錄 (Previous Result)
 * 
 * @param {Array<ParsedPage>} pages - PDF 各頁解析陣列
 * @returns {Array<LabDeviceReport>} 設備檢驗報告陣列
 * 
 * @example
 * const labReports = extractLabReportsFromPages(parsedPages);
 */
export const extractLabReportsFromPages = (pages) => {
  try {
    if (!Array.isArray(pages)) return [];

    return pages.map(page => {
      const items = page.items || [];

      // 1. 識別設備名稱與檢驗時間
      let deviceName = '未知設備';
      let testDateTime = '';
      let rawHeader = '';

      const deviceItem = items.find(item => {
        const s = (item.str || '').trim();
        return /^(ProCyte\s+Dx|Catalyst\s+One|LaserCyte|IDEXX|VetTest)/i.test(s);
      });

      if (deviceItem) {
        rawHeader = deviceItem.str.trim();
        const headerMatch = rawHeader.match(/^([^(]+)(?:\((.*)\))?/);
        if (headerMatch) {
          deviceName = headerMatch[1].trim();
          testDateTime = headerMatch[2] ? headerMatch[2].trim() : '';
        }
      }

      // 提取歷史檢驗時間 (通常位於 X >= 460, Y 介於 650 與 685 之間)
      const prevDateItems = items.filter(item => {
        const [, , , , x, y] = item.transform || [0, 0, 0, 0, 0, 0];
        const s = (item.str || '').trim();
        return x >= 460 && y >= 650 && y <= 685 && s && !s.includes('檢驗') && !s.includes('高');
      }).sort((a, b) => {
        const [, , , , , ya] = a.transform || [0, 0, 0, 0, 0, 0];
        const [, , , , , yb] = b.transform || [0, 0, 0, 0, 0, 0];
        return yb - ya;
      });
      const previousDateTime = prevDateItems.map(i => i.str.trim()).join(' ');

      // 2. 依照 Y 座標群組化 (容差 2.5pt)
      const rowsMap = new Map();
      const TOLERANCE = 2.5;

      items.forEach(item => {
        const text = (item.str || '').trim();
        if (!text) return;

        const [, , , , x, y] = item.transform || [0, 0, 0, 0, 0, 0];

        // 排除頁首病歷資訊、表頭與頁尾資訊
        if (y > 670 || y < 100) return;
        if (/^(檢驗項目|檢驗結果|參考區間|低|參考範圍|高)$/.test(text)) return;
        if (text.includes('寵物名字') || text.includes('畜別') || text.includes('已列印') || text.includes('頁數')) return;
        if (x >= 480 && y >= 650 && y <= 685) return;

        let matchedY = null;
        for (const existingY of rowsMap.keys()) {
          if (Math.abs(existingY - y) <= TOLERANCE) {
            matchedY = existingY;
            break;
          }
        }

        if (matchedY === null) {
          matchedY = y;
          rowsMap.set(matchedY, []);
        }

        rowsMap.get(matchedY).push({ text, x, y });
      });

      // 3. 排序 Y 座標（由上至下）
      const sortedYKeys = Array.from(rowsMap.keys()).sort((a, b) => b - a);
      const tests = [];

      sortedYKeys.forEach(yKey => {
        const rowItems = rowsMap.get(yKey).sort((a, b) => a.x - b.x);

        // X 座標精確欄位映射：
        // 項目名稱: x < 120
        // 當次檢驗結果 (數值+單位): 120 <= x < 185
        // 參考區間: 185 <= x < 350
        // 歷史檢驗值: x >= 460
        const itemCol = rowItems.find(i => i.x < 120);
        const resultCol = rowItems.find(i => i.x >= 120 && i.x < 185);
        const refIntervalCol = rowItems.find(i => i.x >= 185 && i.x < 350);
        const prevCol = rowItems.find(i => i.x >= 460);

        if (itemCol) {
          const rawResult = resultCol ? resultCol.text : '';
          const { value, unit } = parseValueAndUnit(rawResult);

          const rawPrevResult = prevCol ? prevCol.text : '';
          const prevParsed = parseValueAndUnit(rawPrevResult);

          const referenceInterval = refIntervalCol ? refIntervalCol.text.trim() : '';

          tests.push({
            item: itemCol.text,
            rawResult,
            value,
            unit,
            referenceInterval,
            previousResult: rawPrevResult,
            previousValue: prevParsed.value,
            previousUnit: prevParsed.unit
          });
        }
      });

      return {
        pageNumber: page.pageNumber,
        deviceName,
        testDateTime,
        previousDateTime,
        rawHeader,
        tests
      };
    }).filter(report => report.tests.length > 0 || report.deviceName !== '未知設備');
  } catch (error) {
    console.error('[pdfLabParser] extractLabReportsFromPages error:', error);
    return [];
  }
};

/**
 * 將解析後的頁面與報告資料，組裝成符合 HIS 系統標準的檢驗報告陣列 (exam_reports)
 * 包含設備 ID 判定 (Catalyst One=3, ProCyte Dx=4)、exam_result 鍵值對映射與數值型別轉換
 * 
 * @param {Array<ParsedPage>} pages - PDF 解析頁面資料
 * @param {Array<LabDeviceReport>} labReports - 設備解析報告集合
 * @returns {Array<StorageExamReport>} 設備檢驗報告陣列 (exam_reports)
 * 
 * @example
 * const examReports = formatToStorageJson(pages, labReports);
 */
export const formatToStorageJson = (pages, labReports) => {
  try {
    const examReports = (labReports || []).map(report => {
      let deviceId = 0;
      let deviceType = 'UNKNOWN';

      if (report.deviceName.includes('Catalyst')) {
        deviceId = 3;
        deviceType = 'CHEM';
      } else if (report.deviceName.includes('ProCyte')) {
        deviceId = 4;
        deviceType = 'CBC';
      }

      // 產生直接供 HIS 檢驗儲存的 Key-Value 物件 (exam_result)
      const examResult = {};
      report.tests.forEach(t => {
        const numVal = parseFloat(t.value);
        examResult[t.item] = !isNaN(numVal) && String(t.value).trim() === String(numVal) ? numVal : t.value;
      });

      return {
        exam_device_id: deviceId,
        device_name: report.deviceName,
        device_type: deviceType,
        page_number: report.pageNumber,
        exam_datetime: report.testDateTime || '',
        previous_datetime: report.previousDateTime || '',
        exam_result: examResult,
        items: report.tests.map(t => {
          const num = parseFloat(t.value);
          const prevNum = parseFloat(t.previousValue);
          return {
            item: t.item,
            value: !isNaN(num) && String(t.value).trim() === String(num) ? num : t.value,
            unit: t.unit || '',
            reference_interval: t.referenceInterval || '',
            previous_result: t.previousResult || '',
            previous_value: !isNaN(prevNum) && String(t.previousValue).trim() === String(prevNum) ? prevNum : t.previousValue,
            raw: t.rawResult || ''
          };
        })
      };
    });

    return examReports;
  } catch (error) {
    console.error('[pdfLabParser] formatToStorageJson error:', error);
    return [];
  }
};

/**
 * 處理已經由 PDF.js 或其他工具解析出的頁面資料陣列
 * 直接產出純 exam_reports 檢驗報告清單
 * 
 * @param {Array<ParsedPage>} pagesData - 頁面資料陣列
 * @returns {{ labReports: Array<LabDeviceReport>, exam_reports: Array<StorageExamReport>, storageData: Array<StorageExamReport> }}
 * 
 * @example
 * const { exam_reports } = parsePagesData(pagesData);
 */
export const parsePagesData = (pagesData) => {
  try {
    const labReports = extractLabReportsFromPages(pagesData);
    const examReports = formatToStorageJson(pagesData, labReports);
    return {
      labReports,
      exam_reports: examReports,
      storageData: examReports
    };
  } catch (error) {
    console.error('[pdfLabParser] parsePagesData error:', error);
    return {
      labReports: [],
      exam_reports: [],
      storageData: []
    };
  }
};

/**
 * 傳入 PDF 檔案物件 (File / Blob / ArrayBuffer)，執行完整解析並回傳結構化資料
 * 
 * @param {File|Blob|ArrayBuffer} file - PDF 檔案實體物件或 ArrayBuffer
 * @returns {Promise<ParsedPdfResult>} 包含原始 Document、各頁面陣列與純 exam_reports 檢驗報告清單
 * 
 * @example
 * const result = await parsePdfFile(selectedFile);
 * console.log(result.exam_reports); // 取得設備檢驗報告陣列
 */
export const parsePdfFile = async (file) => {
  try {
    if (!file) throw new Error('未提供有效的 PDF 檔案');

    let fileArrayBuffer;
    if (file instanceof ArrayBuffer) {
      fileArrayBuffer = file;
    } else if (file.arrayBuffer && typeof file.arrayBuffer === 'function') {
      fileArrayBuffer = await file.arrayBuffer();
    } else {
      throw new Error('不支援的檔案格式，請提供 File、Blob 或 ArrayBuffer');
    }

    const loadingTask = pdfjsLib.getDocument({ data: fileArrayBuffer });
    const pdf = await loadingTask.promise;

    const totalPages = pdf.numPages;
    const pagesData = [];
    let totalChars = 0;

    for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
      const page = await pdf.getPage(pageNum);
      const textContent = await page.getTextContent();

      const items = textContent.items.map(item => ({
        str: item.str,
        dir: item.dir,
        width: item.width,
        height: item.height,
        transform: item.transform
      }));

      const fullText = items.map(item => item.str).join(' ');
      totalChars += fullText.length;

      pagesData.push({
        pageNumber: pageNum,
        fullText,
        items
      });
    }

    const { labReports, exam_reports } = parsePagesData(pagesData);

    const metaData = {
      fileName: file.name || 'document.pdf',
      fileSize: formatFileSize(file.size || fileArrayBuffer.byteLength || 0),
      totalPages,
      totalCharacters: totalChars
    };

    return {
      pdfDoc: pdf,
      parsedPages: pagesData,
      labReports,
      exam_reports,
      storageData: exam_reports,
      metaData
    };
  } catch (error) {
    console.error('[pdfLabParser] parsePdfFile error:', error);
    throw error;
  }
};

/**
 * 預設匯出物件：整合所有 PDF 檢驗報告解析核心函式
 */
export default {
  formatFileSize,
  parseValueAndUnit,
  extractPatientInfo,
  extractLabReportsFromPages,
  formatToStorageJson,
  parsePagesData,
  parsePdfFile
};

